import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'De l\'import au résultat — un parcours guidé de 15 minutes',
    summary: 'Un parcours complet de bout en bout : créer une communauté, importer un fichier, obtenir une prévision et un premier résultat d\'optimisation.',
    body: (
      <>
        <p>
          Cet article relie tout entre eux. Suivez-le une fois et vous aurez utilisé chaque outil
          au moins une fois. Il suppose que vous disposez d'un fichier de consommation horaire pour
          un seul bâtiment.
        </p>

        <ol>
          <li>
            <strong>Créer une communauté.</strong> Tableau de bord → <em>Communautés</em> →
            {' '}<em>Nouvelle communauté</em>. Donnez-lui un nom et choisissez le pays et la devise. Enregistrez.
          </li>
          <li>
            <strong>Ajouter au moins un bâtiment.</strong> Ouvrez la communauté → <em>Bâtiments</em> →
            {' '}<em>Ajouter un bâtiment</em>. Le nom du bâtiment, son type et un code postal suffisent pour commencer.
          </li>
          <li>
            <strong>Importer votre fichier de charge.</strong> Communauté → <em>Gestion des Données</em> → choisissez
            {' '}<em>Building Load Profile</em>, sélectionnez le bâtiment, déposez le CSV. Consultez
            {' '}<ArticleLink to="data-upload-how">Importer un fichier, étape par étape</ArticleLink> si
            la page vous est nouvelle.
          </li>
          <li>
            <strong>(Recommandé) Importer un fichier tarifaire.</strong> Sans les prix, les résultats de coûts
            seront uniquement indicatifs. Voir <ArticleLink to="data-tariff">Fichiers tarifaires</ArticleLink>.
          </li>
          <li>
            <strong>Lancer une prévision.</strong> Barre latérale de la communauté → <em>Prévision</em>. La page comporte
            deux onglets — <em>Court terme (Day-Ahead)</em> et <em>Long terme (mensuel)</em>. Choisissez un
            bâtiment, cliquez sur <em>Lancer la prévision</em>. Voir
            {' '}<ArticleLink to="analysis-forecast">Lancer une prévision</ArticleLink>.
          </li>
          <li>
            <strong>Lancer une optimisation.</strong> Barre latérale de la communauté → <em>Optimisation</em> (la
            page elle-même est intitulée « Optimisation »). Choisissez un objectif (par ex. <em>Minimiser le coût</em>)
            et cliquez sur <em>Lancer l'optimisation Day-Ahead</em>. Voir
            {' '}<ArticleLink to="analysis-optimisation">Lancer une optimisation</ArticleLink>.
          </li>
          <li>
            <strong>Lire les résultats.</strong> Chiffres, graphiques et économies apparaissent sur la même page.
            La signification de chaque KPI se trouve dans <ArticleLink to="results-glossary">le glossaire</ArticleLink> ;
            l'interprétation de chaque graphique est détaillée dans le reste de cette section.
          </li>
        </ol>

        <Tip>
          La plateforme affiche des résultats illustratifs clairement identifiés si un service est indisponible ou
          si les données requises n'ont pas été importées. Si un résultat semble suspicieusement rond (par ex.
          exactement 100 €/an), vérifiez la présence d'une mention « données d'exemple » sur la page.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Lancer une prévision',
    summary: 'Prédire les prochaines heures/jours de consommation ou de production d\'un bâtiment ou d\'une communauté.',
    body: (
      <>
        <p>
          Une <strong>prévision</strong> utilise vos données passées pour prédire la charge ou la production future.
          Utilisez-la pour planifier les opérations, piloter une optimisation Day-Ahead, ou simplement visualiser
          à quoi ressemblera demain — ou le trimestre prochain.
        </p>

        <h5 className="mt-3">Avant de lancer</h5>
        <ul>
          <li>La communauté possède au moins un bâtiment avec un <strong>Building Load Profile</strong>
            importé (plus l'historique est long, meilleure est la prévision — au moins 3 mois de données sont
            recommandés pour des prévisions fiables, idéalement un an).</li>
          <li>Pour les prévisions <em>solaires</em> ou <em>éoliennes</em>, vous avez également besoin d'un fichier
            de production (profil PV/éolien de la communauté ou le solaire en toiture du bâtiment). Un
            {' '}<ArticleLink to="data-weather">fichier météo</ArticleLink> est optionnel mais améliore
            la précision de 15 à 30 %.</li>
        </ul>

        <h5 className="mt-3">Étape par étape</h5>
        <ol>
          <li>Ouvrez la communauté → choisissez <strong>Prévisions</strong> dans la barre latérale.</li>
          <li>Choisissez le <strong>mode</strong> depuis les deux onglets en haut de la page :
            <ul>
              <li><strong>Court terme (Day-Ahead)</strong> — prédit les 24 prochaines heures à partir d'une
                date cible choisie (jusqu'à 7 jours à l'avance).</li>
              <li><strong>Long terme (mensuel)</strong> — projette des chiffres mensuels sur un horizon de
                votre choix : <em>3 mois</em>, <em>6 mois</em> ou <em>12 mois</em>.</li>
            </ul>
          </li>
          <li>Choisissez le(s) <strong>équipement(s)</strong> à prévoir : la charge d'un bâtiment, la
            Production PV de la communauté ou la Production éolienne de la communauté. Vous pouvez en
            cocher plusieurs.</li>
          <li>Cliquez sur <strong>Lancer la prévision</strong> (le libellé du bouton s'adapte au nombre
            d'équipements sélectionnés). Un indicateur de chargement affiche <em>Prévision en cours…</em>
            pendant le traitement.</li>
          <li>Les résultats apparaissent dans le panneau de droite. Voir
            {' '}<ArticleLink to="results-forecast">Lire le graphique de prévision</ArticleLink>
            {' '}pour la signification de chaque courbe et bande.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="La page Prévisions : choisissez un onglet, sélectionnez les équipements, lancez."
        />

        <Tip>
          Si la même prévision semble très différente d'une exécution à l'autre, vous disposez probablement de
          très peu de données d'entrée. Ajoutez davantage d'historique et relancez. Consultez
          {' '}<ArticleLink to="tb-forecast-flat">« Ma prévision semble plate ou incorrecte »</ArticleLink>
          {' '}dans le dépannage si les résultats semblent toujours incorrects.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Lancer une optimisation (Optimisation)',
    summary: 'Décider quand importer, exporter, charger ou décharger — pour minimiser le coût ou maximiser l\'autosuffisance.',
    body: (
      <>
        <p>
          L'outil <strong>Optimisation</strong> trouve le meilleur programme pour les équipements flexibles
          de votre communauté — quand charger ou décharger une batterie, quand charger des véhicules électriques,
          quand importer depuis le réseau et quand exporter — selon un objectif que vous choisissez.
        </p>

        <h5 className="mt-3">Avant de lancer</h5>
        <ul>
          <li>Une communauté avec au moins un fichier de <strong>charge</strong> importé.</li>
          <li>Un fichier <strong>tarifaire</strong> (l'optimiseur a besoin des prix pour distinguer les heures
            bon marché des heures coûteuses). Voir <ArticleLink to="data-tariff">Fichiers tarifaires</ArticleLink>.</li>
          <li>Optionnel mais recommandé : un fichier de <strong>production</strong> et tout
            {' '}<strong>équipement</strong> (PV, batterie, VE) renseigné pour la communauté.</li>
        </ul>

        <h5 className="mt-3">Étape par étape</h5>
        <ol>
          <li>Ouvrez la communauté → choisissez <strong>Optimisation</strong> dans la barre latérale. (La page
            est également intitulée « Optimisation » — c'est le même endroit.)</li>
          <li>Choisissez un <strong>objectif</strong> :
            <ul>
              <li><em>Minimiser le coût</em> — acheter quand les prix sont bas, vendre quand ils sont élevés.
                Par défaut pour la plupart des communautés.</li>
              <li><em>Maximiser l'autosuffisance</em> — utiliser autant que possible votre propre production,
                même si le coût est légèrement plus élevé.</li>
              <li><em>Maximiser l'autoconsommation</em> — minimiser les exports de votre production.</li>
            </ul>
          </li>
          <li>L'optimisation calcule un programme journalier (24 heures) pour le lendemain.</li>
          <li>Vérifiez la liste des équipements (PV, batterie, bornes de recharge VE). Les paramètres de
            batterie tels que <em>l'état de charge min/max</em> et le <em>rendement aller-retour</em> doivent
            correspondre à la réalité — modifiez-les si nécessaire.</li>
          <li>Cliquez sur <strong>Lancer l'optimisation Day-Ahead</strong>. Un indicateur de chargement affiche
            <em>Optimisation en cours…</em> (un message « Résolution du problème MILP » peut également
            apparaître — c'est le solveur mathématique en action). L'exécution pour quelques bâtiments se
            termine généralement en moins d'une minute.</li>
          <li>Une fois terminée, le panneau de résultats apparaît en dessous — cartes KPI en haut, graphiques
            horaires Bilan Énergétique et Programme Batterie en dessous. Voir
            {' '}<ArticleLink to="results-optimisation-kpis">Lire les KPI d'optimisation</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="La page Optimisation : choisissez un objectif, vérifiez les équipements, lancez."
        />

        <Warn>
          Si l'exécution se termine avec « infeasible », les contraintes ne peuvent pas toutes être satisfaites
          simultanément (par exemple, la batterie est trop petite pour la demande). Voir
          {' '}<ArticleLink to="tb-optimisation-infeasible">« L'optimisation indique infeasible »</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
